import {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {Modal} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'

type RoleRow = {id: number; code: string; name: string}

type Props = {
  show: boolean
  onHide: () => void
  onCreated: () => void
}

const schema = Yup.object({
  email: Yup.string().email().required(),
  name: Yup.string().max(120),
  role_id: Yup.number().min(1, 'Select a role').required(),
})

export const InviteAdminModal: FC<Props> = ({show, onHide, onCreated}) => {
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!show) return
    const token = AdminAuth()
    if (!token) return
    void axios
      .get(`${apiUrl}/admin/roles`, {headers: {'x-access-token': token}})
      .then((res) => {
        const rows = res.data?.data as RoleRow[] | undefined
        setRoles(Array.isArray(rows) ? rows : [])
      })
      .catch(() => setRoles([]))
  }, [show])

  const formik = useFormik({
    initialValues: {email: '', name: '', role_id: 0},
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setErr('')
      const token = AdminAuth()
      if (!token) return
      try {
        await axios.post(
          `${apiUrl}/admin/users/invite`,
          {
            email: values.email.trim(),
            name: values.name.trim() || undefined,
            role_id: values.role_id,
          },
          {headers: {'x-access-token': token}}
        )
        resetForm()
        onCreated()
        onHide()
      } catch (e: any) {
        const msg =
          e?.response?.data?.message || e?.response?.data?.error || 'Could not send invite.'
        setErr(typeof msg === 'string' ? msg : 'Could not send invite.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (show && roles.length && formik.values.role_id === 0) {
      const first = roles.find((r) => r.code !== 'super_admin') || roles[0]
      if (first) void formik.setFieldValue('role_id', first.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, roles])

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Invite admin</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err ? <div className='alert alert-danger'>{err}</div> : null}
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className='mb-5'>
            <label className='form-label required'>Email</label>
            <input className='form-control' type='email' {...formik.getFieldProps('email')} />
            {formik.touched.email && formik.errors.email && (
              <div className='text-danger fs-7 mt-1'>{formik.errors.email}</div>
            )}
          </div>
          <div className='mb-5'>
            <label className='form-label'>Name</label>
            <input className='form-control' {...formik.getFieldProps('name')} />
          </div>
          <div className='mb-5'>
            <label className='form-label required'>Role</label>
            <select
              className='form-select'
              name='role_id'
              value={formik.values.role_id || ''}
              onChange={formik.handleChange}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.code})
                </option>
              ))}
            </select>
          </div>
          <div className='d-flex justify-content-end gap-2'>
            <button type='button' className='btn btn-light' onClick={onHide}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
              Send invite
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}
